import { useMutation } from '@apollo/client/react';
import type { FC } from 'react';

import { ProductCategoriesTrackingAction, ProductCategoriesTrackingCategory } from '@zen/Products/types';
import { useNotification } from '@zen/utils/hooks/useNotification';
import useTracking from '@zen/utils/hooks/useTracking';
import type { IOkOrErrorResult } from '@zen/utils/OkOrErrorResult';
import { performFormMutation } from '@zen/utils/performMutation';

import ProductCategoryForm from '../components/ProductCategoryForm';
import {
  UpdateProductCategoryDocument,
  type UpdateProductCategoryMutationResult,
  type UpdateProductCategoryMutationVariables
} from '../graphql';
import type { CoreProductCategory } from '../types';

interface Props {
  item: CoreProductCategory;
  onClose: () => void;
}

const EditProductCategory: FC<Props> = ({ item, onClose }) => {
  const { addSuccess, addError } = useNotification();
  const { trackEvent } = useTracking();
  const [updateCategory] = useMutation<UpdateProductCategoryMutationResult, UpdateProductCategoryMutationVariables>(
    UpdateProductCategoryDocument
  );

  const trackCategoryUpdate = (categoryName: string): void => {
    trackEvent({
      category: ProductCategoriesTrackingCategory,
      action: ProductCategoriesTrackingAction.CATEGORY_UPDATED,
      label: categoryName,
      properties: {
        categoryName
      }
    });
  };

  const handleSubmit = async ({ name }: Pick<CoreProductCategory, 'name'>): Promise<IOkOrErrorResult> => {
    trackCategoryUpdate(name);

    return performFormMutation({
      mutationFn: () =>
        updateCategory({
          awaitRefetchQueries: true,
          refetchQueries: ['productCategories'],
          variables: {
            input: {
              id: item.id,
              name
            }
          }
        }),
      onError: () => addError()
    });
  };

  const handleSuccess = (): void => {
    addSuccess('Product category updated successfully.');
    onClose();
  };

  return (
    <ProductCategoryForm
      buttonText="Save changes"
      initialValues={item}
      onCancel={onClose}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
    />
  );
};

export default EditProductCategory;
